import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { importGupshupTemplates } from "../../api/client";
import CreateWhatsappTemplateDialog from "./CreateWhatsappTemplateDialog";
import { nextRejectedTemplateName } from "../../utils/whatsappTemplateLifecycle";

const FILTERS = ["all", "pending", "action", "ready"];

function rejectCopy(t, code, correctCategory) {
  const key = code || "fallback";
  const title = t(`channelForm.waCloudUi.templateReject.${key}.title`, {
    defaultValue: t("channelForm.waCloudUi.templateReject.fallback.title"),
  });
  const cause = t(`channelForm.waCloudUi.templateReject.${key}.cause`, {
    defaultValue: t("channelForm.waCloudUi.templateReject.fallback.cause"),
    category: correctCategory || "",
  });
  const fix = t(`channelForm.waCloudUi.templateReject.${key}.fix`, {
    defaultValue: t("channelForm.waCloudUi.templateReject.fallback.fix"),
    category: correctCategory || "",
  });
  return { title, cause, fix };
}

function TemplateRow({ tpl, t, expanded, onToggle, onCreateFrom }) {
  const kind = tpl.statusKind || "unknown";
  const chip =
    kind === "approved"
      ? { label: t("channelForm.waCloudUi.templateKindApproved"), color: "success" }
      : kind === "pending"
        ? { label: t("channelForm.waCloudUi.templateKindPending"), color: "warning" }
        : kind === "rejected"
          ? { label: t("channelForm.waCloudUi.templateKindRejected"), color: "error" }
          : kind === "paused"
            ? { label: t("channelForm.waCloudUi.templateKindPaused"), color: "warning" }
            : kind === "disabled"
              ? { label: t("channelForm.waCloudUi.templateKindDisabled"), color: "error" }
              : kind === "appeal"
                ? { label: t("channelForm.waCloudUi.templateKindAppeal"), color: "warning" }
                : kind === "deleted"
                  ? { label: t("channelForm.waCloudUi.templateKindDeleted"), color: "default" }
                  : tpl.status
                    ? { label: String(tpl.status), color: "default" }
                    : null;
  const hasQualityWarning = tpl.quality === "YELLOW" || tpl.quality === "RED";
  const needsDetail =
    kind === "rejected" || kind === "paused" || kind === "disabled" || kind === "appeal" || hasQualityWarning;
  const reject = kind === "rejected" ? rejectCopy(t, tpl.rejectedReason, tpl.correctCategory) : null;

  return (
    <>
      <ListItem
        onClick={needsDetail ? onToggle : undefined}
        sx={{
          py: 1.5,
          px: 2,
          cursor: needsDetail ? "pointer" : "default",
          alignItems: "flex-start",
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <ListItemText
          primary={
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={600} component="span">
                {tpl.name || `Template #${tpl.id}`}
              </Typography>
              {tpl.languageCode ? (
                <Chip size="small" label={tpl.languageCode} variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
              ) : null}
              {tpl.category ? (
                <Chip size="small" label={tpl.category} variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
              ) : null}
              {chip ? (
                <Chip size="small" label={chip.label} color={chip.color} sx={{ height: 20, fontSize: "0.7rem" }} />
              ) : null}
              {hasQualityWarning ? (
                <Chip
                  size="small"
                  label={
                    tpl.quality === "RED"
                      ? t("channelForm.waCloudUi.templateQualityRedChip")
                      : t("channelForm.waCloudUi.templateQualityYellowChip")
                  }
                  color={tpl.quality === "RED" ? "error" : "warning"}
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              ) : null}
              {needsDetail ? (expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />) : null}
            </Stack>
          }
          secondary={
            <>
              {kind === "pending" ? (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                  {t("channelForm.waCloudUi.templatePendingHint")}
                </Typography>
              ) : null}
              {tpl.preview ? (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: "text.secondary",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {tpl.preview}
                </Typography>
              ) : null}
            </>
          }
        />
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>
          {kind === "rejected" && reject ? (
            <Alert severity="error">
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {reject.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                {reject.cause}
              </Typography>
              <Typography variant="body2">{reject.fix}</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, mb: 1 }}>
                {t("channelForm.waCloudUi.templateRejectCreateNew")}
              </Typography>
              {onCreateFrom ? (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    onCreateFrom(tpl);
                  }}
                >
                  {t("channelForm.waCloudUi.createFromRejected")}
                </Button>
              ) : null}
            </Alert>
          ) : null}
          {kind === "paused" ? (
            <Alert severity="warning">{t("channelForm.waCloudUi.templatePausedHint")}</Alert>
          ) : null}
          {kind === "disabled" ? (
            <Alert severity="error">{t("channelForm.waCloudUi.templateDisabledHint")}</Alert>
          ) : null}
          {kind === "appeal" ? (
            <Alert severity="info">{t("channelForm.waCloudUi.templateAppealHint")}</Alert>
          ) : null}
          {tpl.quality === "YELLOW" || tpl.quality === "RED" ? (
            <Alert severity={tpl.quality === "RED" ? "error" : "warning"} sx={{ mt: 1 }}>
              {tpl.quality === "RED"
                ? t("channelForm.waCloudUi.templateQualityRed")
                : t("channelForm.waCloudUi.templateQualityYellow")}
            </Alert>
          ) : null}
        </Box>
      </Collapse>
    </>
  );
}

export default function ChannelWhatsappTemplatesSection({
  channelId,
  templates,
  loading,
  disabled,
  onReload,
}) {
  const { t } = useTranslation("settings");
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(null);
  const [createNotice, setCreateNotice] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const counts = useMemo(() => {
    const list = Array.isArray(templates) ? templates : [];
    return {
      all: list.length,
      pending: list.filter((tpl) => tpl.statusKind === "pending").length,
      action: list.filter((tpl) =>
        ["rejected", "paused", "disabled", "appeal"].includes(tpl.statusKind)
      ).length,
      ready: list.filter((tpl) => tpl.usable).length,
    };
  }, [templates]);

  const visible = useMemo(() => {
    const list = Array.isArray(templates) ? templates : [];
    const filtered =
      filter === "pending"
        ? list.filter((tpl) => tpl.statusKind === "pending")
        : filter === "action"
          ? list.filter((tpl) => ["rejected", "paused", "disabled", "appeal"].includes(tpl.statusKind))
          : filter === "ready"
            ? list.filter((tpl) => tpl.usable)
            : list;
    const rank = (kind) =>
      kind === "rejected" ? 0 : kind === "paused" || kind === "disabled" || kind === "appeal" ? 1 : kind === "pending" ? 2 : 3;
    return [...filtered].sort((a, b) => rank(a.statusKind) - rank(b.statusKind));
  }, [templates, filter]);

  const reloadRef = useRef(onReload);
  reloadRef.current = onReload;
  useEffect(() => {
    if (!channelId || !counts.pending) return undefined;
    const timer = setInterval(() => {
      reloadRef.current?.();
    }, 20000);
    return () => clearInterval(timer);
  }, [channelId, counts.pending]);

  const handleImport = async () => {
    setImportError(null);
    setImportResult(null);
    setCreateNotice(null);
    if (!channelId) {
      setImportError(t("channelForm.waCloudUi.importTemplatesNeedSave"));
      return;
    }
    try {
      setImporting(true);
      const result = await importGupshupTemplates(channelId);
      setImportResult(result || {});
      if (!result?.warning) onReload?.();
    } catch (error) {
      setImportError(
        error?.response?.data?.message ||
          error?.message ||
          t("channelForm.waCloudUi.importTemplatesError")
      );
    } finally {
      setImporting(false);
    }
  };

  const handleCreate = (draft = null) => {
    setImportError(null);
    setCreateNotice(null);
    if (!channelId) {
      setImportError(t("channelForm.waCloudUi.createTemplateNeedSave"));
      return;
    }
    setCreateDraft(draft);
    setCreateOpen(true);
  };

  const handleCreateFromRejected = (tpl) => {
    handleCreate({
      name: nextRejectedTemplateName(tpl.elementName || tpl.name),
      languageCode: tpl.languageCode || "pt_BR",
      category: String(tpl.correctCategory || tpl.category || "UTILITY").toUpperCase(),
      content: tpl.bodyText || tpl.preview || "",
    });
  };

  const didAutoExpand = useRef(false);
  useEffect(() => {
    if (didAutoExpand.current) return;
    const firstRejected = (Array.isArray(templates) ? templates : []).find(
      (tpl) => tpl.statusKind === "rejected"
    );
    if (firstRejected) {
      setExpandedId(firstRejected.id);
      didAutoExpand.current = true;
    }
  }, [templates]);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary">
              {t("channelForm.waCloudUi.templatesSectionTitle")}
            </Typography>
            {!loading && counts.all > 0 ? (
              <Chip
                size="small"
                label={t("channelForm.waCloudUi.templatesCountChip", { count: counts.all })}
                variant="outlined"
              />
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {t("channelForm.waCloudUi.templatesSectionCaption")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }} flexWrap="wrap">
          <Button variant="contained" size="small" onClick={() => handleCreate()} disabled={disabled}>
            {t("channelForm.waCloudUi.createTemplateButton")}
          </Button>
          <Button variant="outlined" size="small" onClick={handleImport} disabled={importing || disabled}>
            {importing
              ? t("channelForm.waCloudUi.importTemplatesLoading")
              : t("channelForm.waCloudUi.importTemplatesButton")}
          </Button>
        </Stack>
      </Stack>

      {importError ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {importError}
        </Alert>
      ) : null}
      {createNotice ? (
        <Alert severity={createNotice.warning ? "info" : "success"} sx={{ mt: 1.5 }}>
          {createNotice.warning
            ? createNotice.warning
            : t("channelForm.waCloudUi.createTemplateSuccess", { name: createNotice.name || "" })}
        </Alert>
      ) : null}
      {importResult ? (
        <Alert severity={importResult.warning ? "info" : "success"} sx={{ mt: 1.5 }}>
          {importResult.warning
            ? importResult.warning
            : t("channelForm.waCloudUi.templatesSyncSummary", {
                imported: importResult.imported ?? 0,
                updated: importResult.updated ?? 0,
                total: importResult.totalRemote ?? 0,
              })}
        </Alert>
      ) : null}

      {counts.pending > 0 ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t("channelForm.waCloudUi.templatePendingBanner", { count: counts.pending })}
        </Alert>
      ) : null}
      {counts.action > 0 ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t("channelForm.waCloudUi.templateRejectedBanner", { count: counts.action })}
        </Alert>
      ) : null}

      {counts.all > 0 ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
          {FILTERS.map((id) => (
            <Chip
              key={id}
              size="small"
              variant={filter === id ? "filled" : "outlined"}
              color={filter === id ? "primary" : "default"}
              label={t(`channelForm.waCloudUi.templateFilter_${id}`, { count: counts[id] })}
              onClick={() => setFilter(id)}
            />
          ))}
        </Stack>
      ) : null}

      <Box sx={{ mt: 1.5 }}>
        {loading ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" height={52} sx={{ mb: i < 5 ? 1 : 0, borderRadius: 1 }} />
            ))}
          </Paper>
        ) : visible.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center", bgcolor: "action.hover" }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {counts.all === 0
                ? t("channelForm.waCloudUi.templatesEmptyTitle")
                : t(`channelForm.waCloudUi.templatesEmptyFilter_${filter}`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {counts.all === 0
                ? t("channelForm.waCloudUi.templatesEmptyHint")
                : t("channelForm.waCloudUi.templatesEmptyFilterHint")}
            </Typography>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{ maxHeight: 420, overflow: "auto", borderRadius: 2, border: "1px solid", borderColor: "divider" }}
          >
            <List disablePadding>
              {visible.map((tpl, idx) => (
                <Box
                  key={tpl.id}
                  sx={{ bgcolor: idx % 2 === 0 ? "transparent" : "action.hover" }}
                  divider={idx < visible.length - 1}
                >
                  <TemplateRow
                    tpl={tpl}
                    t={t}
                    expanded={expandedId === tpl.id}
                    onToggle={() => setExpandedId((cur) => (cur === tpl.id ? null : tpl.id))}
                    onCreateFrom={handleCreateFromRejected}
                  />
                </Box>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      <CreateWhatsappTemplateDialog
        open={createOpen}
        channelId={channelId}
        initialDraft={createDraft}
        onClose={() => {
          setCreateOpen(false);
          setCreateDraft(null);
        }}
        onCreated={(result) => {
          setCreateNotice({
            warning: result?.warning,
            name: result?.template?.elementName || "",
          });
          setFilter("pending");
          onReload?.();
        }}
      />
    </Box>
  );
}
